// SEP-2 / E0 — EntityMediaField: an accessible image upload / replace field for guided entity forms.
// Bytes go through the REAL hardened, deployed creator upload server (POST /v1/creator/assets —
// magic-byte MIME sniff, EXIF strip, size caps), exactly like AssetLibraryPanel; the resulting public
// URL is handed back to the parent form via onChange. The parent (e.g. ManageProducts) persists it
// onto the entity row (PATCH data.mediaUrl). No optimistic lie: the thumbnail only appears after the
// server returns a URL.
//
// a11y: the picker is a real <button> that opens the native file dialog (keyboard-operable, NOT
// drag-only), the thumbnail's alt comes from a required-by-default alt-text <input> with a visible
// <label>, and upload errors are announced via an aria-live region. Category-agnostic: any owner
// entity with an image (product, service, listing) reuses it.
import { useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { ImagePlus, RefreshCw, X, Loader2 } from 'lucide-preact';
import { t } from '../lib/i18n';

/** The value an image field carries: the public URL (null until uploaded) + its alt text. */
export interface MediaValue {
  url: string | null;
  alt?: string;
}

export interface EntityMediaFieldProps {
  /** owner app id (the upload server is creator-session scoped; appId is passed for parity + future
   *  per-app cataloging — the byte upload itself is account-scoped). */
  appId: string;
  /** current value. */
  value: MediaValue;
  /** i18n key for the field label. */
  labelKey: string;
  /** called with the new value after a successful upload, an alt-text edit, or a remove. */
  onChange: (v: MediaValue) => void;
  /** require alt text before the value is considered valid (default true — accessibility + SEO). */
  altRequired?: boolean;
  /** surface a validation error for the alt field (from the parent form's submit attempt). */
  altError?: boolean;
}

export function EntityMediaField({ appId, value, labelKey, onChange, altRequired = true, altError }: EntityMediaFieldProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  void appId; // reserved for future per-app cataloging; byte upload is account-scoped

  async function onPick(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || busy) return;
    setBusy(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await fetch('/v1/creator/assets', { method: 'POST', credentials: 'include', body: fd });
      if (!up.ok) { setErr(t('own.mediaUploadFailed')); return; }
      const { asset } = (await up.json()) as { asset?: { url?: string; key?: string } };
      const url = asset?.url ?? null;
      if (!url) { setErr(t('own.mediaUploadFailed')); return; }
      onChange({ url, alt: value.alt });
    } catch {
      setErr(t('own.mediaUploadFailed'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const hasImage = !!value.url;
  const altId = 'media-alt-field';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>{t(labelKey)}</span>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Thumbnail / placeholder */}
        {hasImage ? (
          <img src={value.url as string} alt={value.alt || ''} style={{ width: 72, height: 72, borderRadius: 12, objectFit: 'cover', border: '1px solid var(--border)', flexShrink: 0 }} />
        ) : (
          <span className="pf-bubble" aria-hidden="true" style={{ width: 72, height: 72, borderRadius: 12, flexShrink: 0 }}>
            <ImagePlus size={22} />
          </span>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180, flex: 1 }}>
          <span style={{ display: 'inline-flex', gap: 7, flexWrap: 'wrap' }}>
            <button type="button" className="pf-btn pf-btn--sm pf-btn--soft" disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? <Loader2 size={13} className="pf-spin" /> : hasImage ? <RefreshCw size={13} /> : <ImagePlus size={13} />}
              {busy ? t('own.mediaUploading') : hasImage ? t('own.mediaReplace') : t('own.mediaUpload')}
            </button>
            {hasImage && !busy && (
              <button type="button" className="pf-btn pf-btn--sm pf-btn--ghost" onClick={() => onChange({ url: null, alt: value.alt })}>
                <X size={13} /> {t('own.mediaRemove')}
              </button>
            )}
          </span>
          {!hasImage && <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('own.mediaEmpty')}</span>}
        </div>
        {/* keyboard-operable + programmatically-clickable file input (hidden, not drag-only) */}
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => void onPick(e)} />
      </div>

      {/* Alt text — a real labelled input, required by default for a11y + SEO. */}
      {hasImage && (
        <label htmlFor={altId} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fg)' }}>
            {t('own.mediaAlt')}{altRequired ? ' *' : ''}
          </span>
          <input
            id={altId}
            className="pf-input"
            type="text"
            value={value.alt || ''}
            aria-required={altRequired ? 'true' : 'false'}
            aria-invalid={altError ? 'true' : 'false'}
            aria-describedby={altError ? `${altId}-err` : `${altId}-hint`}
            onInput={(e) => onChange({ url: value.url, alt: (e.target as HTMLInputElement).value })}
            style={inputStyle}
          />
          {altError
            ? <span id={`${altId}-err`} role="alert" style={{ fontSize: 12, color: 'var(--color-danger)' }}>{t('own.mediaAltRequired')}</span>
            : <span id={`${altId}-hint`} style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('own.mediaAltHint')}</span>}
        </label>
      )}

      {err && <span role="alert" aria-live="assertive" style={{ fontSize: 12.5, color: 'var(--color-danger)' }}>{err}</span>}
    </div>
  );
}

const inputStyle: JSX.CSSProperties = {
  height: 40, padding: '0 12px', fontSize: 14, borderRadius: 10,
  border: '1px solid var(--border)', background: 'var(--bg-el)', color: 'var(--fg)', width: '100%',
};
